'use client';

import { message } from 'antd';
import classNames from 'classnames';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import LottieJumpLoading from '@/components/chat-app/components/LottieJumpLoading';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import { useModel } from '@/hooks/useModel';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import useBiogStore from '@/store/useBiogStore';
import type { BiogWord } from '@/types/visualization';
import { ROUTER_PATH } from '@/utils/router';
import { pluralizeWithCountTName } from '@/utils/utils';

import AboutMe from './component/AboutMe';
import Appearance from './component/Appearance';
import Detail from './component/BiogDetail';
import BiogSpace from './component/BiogSpace';
import VoiceBtn from './component/VoiceBtn';

function Visualization() {
  const { userProfile } = useModel('auth-model');

  const keywords = useBiogStore((state) => state.keywords);
  const updateKeyword = useBiogStore((state) => state.updateKeyword);
  const refreshRankDetail = useBiogStore((state) => state.refreshRankDetail);
  const rankDetail = useBiogStore((state) => state.rankDetail);
  const loadingKeyWord = useBiogStore((state) => state.loadingKeyWord);
  const searchParams = useSearchParams();
  const router = useRouter();
  const paramBiog = useMemo(() => {
    return searchParams.get('chosenBiog');
  }, [searchParams]);
  const [layoutSize] = useStorage('layoutSize');
  const [inMobile, setInMobile] = useState(layoutSize === LAYOUT_SIZE.SMALL);
  const [open, setOpen] = useState(false);
  const [chosenBiog, setChosenBiog] = useState<BiogWord | undefined>(undefined);

  const { t } = useTranslation();

  useEffect(() => {
    setInMobile(layoutSize === LAYOUT_SIZE.SMALL);
  }, [layoutSize]);

  useEffect(() => {
    updateKeyword();
    refreshRankDetail();
  }, []);

  const handleOpen = (state: boolean) => {
    setOpen(state);
  };

  const handleClick = (item: BiogWord) => {
    if (!item.id) {
      message.error(t('error.oops'));

      return;
    }

    setChosenBiog(item);
    setOpen(true);

    router.replace(
      inMobile
        ? `${ROUTER_PATH.personalAIMobile}?chosenBiog=${item.id}`
        : `${ROUTER_PATH.personalAI}?chosenBiog=${item.id}`
    );
  };

  const handleCancel = () => {
    setChosenBiog(undefined);
    setOpen(false);
  };

  return loadingKeyWord ? (
    <div className="flex items-center justify-center h-full">
      <LottieJumpLoading className="!w-16 !h-16" />
    </div>
  ) : (
    <div
      className={classNames(
        'w-full max-w-[1188px] mx-auto px-[60px] pb-7 pt-3 flex flex-col items-center gap-6',
        inMobile ? '!gap-0 !px-4 overflow-auto !pt-4' : ''
      )}
      style={{
        opacity: paramBiog ? '0' : '1',
        background: inMobile
          ? 'linear-gradient(180deg, rgba(198, 190, 244, 0.6) 0%, rgba(249, 223, 215, 0) 100%)'
          : ''
      }}
    >
      <div className="text-2xl leading-8 font-[Calistoga-Regular]">{t('bio.title')}</div>

      <div
        className={classNames(
          'flex items-start justify-center gap-4 flex-col',
          inMobile ? '!block mt-6 w-full' : ''
        )}
      >
        <div
          className={classNames(
            'flex flex-col items-center shrink-0 rounded-[24px] pt-10 pb-6',
            inMobile ? 'w-full px-0 overflow-hidden' : 'w-[552px] px-[48px]'
          )}
          style={{
            background: !inMobile
              ? 'linear-gradient(180deg, #9A82E1 0%, rgba(238, 210, 200, 0) 100%)'
              : ''
          }}
        >
          {/* Icon & MemoryNum */}
          <div className="flex flex-col items-center gap-3 mb-[24px]">
            <div className="flex">
              <div className="w-[52px] h-[52px] rotate-[-8deg] bg-[#FEFDFF]/[0.9] flex items-center justify-center rounded-full !mr-[-6px] shadow-[0px_0px_12px_0px_rgba(255,255,255,0.7)]">
                <img
                  alt=""
                  className="w-full h-full object-cover"
                  src="https://mindverseglobal.mindverse.com/front-img/crm/1737439207363/mebot-bio.png"
                />
              </div>
              <div className="w-[52px] h-[52px] rotate-[8deg] rounded-full flex items-center justify-center border-[2px] border-solid border-[#FEFDFF] shadow-[0px_0px_12px_0px_rgba(255,255,255,0.7)] overflow-hidden">
                <img
                  className="w-[52px] h-[52px] object-cover"
                  src={userProfile?.homepage?.cover || userProfile?.avatar}
                />
              </div>
            </div>
            <div className="text-sm leading-6 text-primary-new-black text-center">
              {t('bio.maximumTip', {
                memoryCount: rankDetail.memoryCount,
                dayNum: rankDetail.dayNum
              })}
            </div>
          </div>

          {/* About Me */}
          <AboutMe className="mb-[24px]" />

          {/* Voice & Appearance */}
          <div
            className={classNames(
              'flex items-center w-full gap-[28px]',
              inMobile ? 'flex-col !gap-3' : ''
            )}
          >
            <VoiceBtn className="basis-1/2" />
            <Appearance className="basis-1/2" />
          </div>

          {/* Divider */}
          <div className="w-full h-[1px] bg-[rgba(254,253,255,0.5)] my-10" />

          {/* Shades Num */}
          <div className="font-medium text-[16px] leading-[28px] text-[#643E54] mr-auto mb-10">
            {t(
              pluralizeWithCountTName(
                // aboutMe ? keywords.length + 1 : keywords.length,
                keywords.length,
                'bio.shadeOfYou'
              ),
              { number: keywords.length }
            )}
          </div>

          {/* BiogSpace */}
          <div className={classNames('flex items-center justify-center relative')}>
            <BiogSpace handleClick={handleClick} keywords={keywords} setOpen={handleOpen} />
          </div>

          {/* Shades */}
          <div className={classNames('flex flex-col w-full items-center relative gap-3 mt-10')}>
            {keywords.length
              ? keywords.map((item) => (
                  <div
                    key={item.id}
                    className="box-border w-full cursor-pointer px-4 py-2 flex flex-col justify-center gap-6 bg-[rgba(254,253,255,0.3)] rounded-[12px] leading-6 transition-colors hover:bg-[rgba(254,253,255,0.6)]"
                    onClick={() => {
                      handleClick({
                        word: item.word,
                        icon: item.icon,
                        weight: item.weight,
                        id: item.id
                      });
                    }}
                  >
                    {item.content}
                  </div>
                ))
              : null}
          </div>

          <div className="flex flex-col items-center text-[#B8AD9B] font-normal text-[14px] leading-[24px] mt-10">
            <div>{t('bio.unlockContent1')}</div>
            <div>{t('bio.unlockContent2')}</div>
          </div>
        </div>

        <Detail
          chosenBiog={chosenBiog}
          handleCancel={handleCancel}
          keywords={keywords}
          open={open}
        />
      </div>
    </div>
  );
}

export default Visualization;
